/**
 * Audio → mouth. The one piece of lipsync logic in the package, so that adding a
 * face never means writing signal processing again.
 *
 * Two layers, deliberately separable:
 *  - {@link MouthAnalyser} — pure DSP over `Float32Array` samples. No Web Audio, no
 *    DOM, no clock of its own. This is the part that is unit-tested.
 *  - {@link MouthDriver} — a thin `AnalyserNode` + requestAnimationFrame wrapper that
 *    feeds the analyser from a live audio graph.
 *
 * Level is always produced. Visemes are best-effort: set `visemes: false` for a
 * cheaper level-only signal, and every face still works.
 */

import type { MouthFrame, Viseme, VisemeId } from './types.js';
import { VISEME_IDS } from './types.js';

/**
 * First/second formant centroids (Hz) for the five VRM vowels (あ aa, い ih, う ou, え ee,
 * お oh), as this analyser measures them on real voices. Classifying a frame is then just
 * "which of these five points is the spectrum nearest to", which is cheap and stateless.
 *
 * Measured, not textbook: the medians over the vowel nuclei of four hangout.chat cast voices
 * (two female, two male; one TTS clip per vowel, e.g. "Me. See. Free. Tea. Me."). The table
 * this replaced held English formants under the VRM names, so `ih` (い) and `ee` (え) were
 * swapped, and without pre-emphasis it read 30 % of real vowels right: "me" never reached the
 * い mouth. These read 63 % of vowels and 74 % of the drawing groups (wide い / round う お /
 * open あ え) right on a voice left out of the fit. See LAYERED_AVATARS.md → "Animation".
 */
const VOWEL_FORMANTS: Record<VisemeId, readonly [f1: number, f2: number]> = {
  aa: [607, 1174],
  ih: [290, 2624],
  ou: [341, 1097],
  ee: [486, 1705],
  oh: [504, 1100],
};

/**
 * +6 dB/octave pre-emphasis (power × f²), as speech analysis does before reading formants:
 * a voice's strong low harmonics otherwise pull both centroids down, and nearly every vowel
 * read as う or お.
 */
const preEmphasis = (f: number) => (f / 1000) ** 2;

/** Log-spaced probe frequencies covering the vowel formant range. */
const PROBES = logSpace(180, 3200, 28);
const F1_BAND: readonly [number, number] = [180, 1000];
const F2_BAND: readonly [number, number] = [820, 3200];

/** Formant tolerance in log-frequency. ~0.3 ≈ a third of an octave. */
const FORMANT_SIGMA = 0.3;

function logSpace(lo: number, hi: number, n: number): Float64Array {
  const out = new Float64Array(n);
  const step = Math.log(hi / lo) / (n - 1);
  for (let i = 0; i < n; i++) out[i] = lo * Math.exp(step * i);
  return out;
}

/** Root mean square amplitude of a block of samples. */
export function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return samples.length ? Math.sqrt(sum / samples.length) : 0;
}

/**
 * Power at a single frequency (Goertzel). Cheaper than an FFT when you only want
 * a couple of dozen bins, and it keeps this file dependency-free.
 */
export function goertzelPower(
  samples: Float32Array,
  sampleRate: number,
  freq: number,
  window?: Float64Array,
): number {
  const n = samples.length;
  if (!n || freq <= 0 || freq >= sampleRate / 2) return 0;
  const coeff = 2 * Math.cos((2 * Math.PI * freq) / sampleRate);
  let s1 = 0;
  let s2 = 0;
  for (let i = 0; i < n; i++) {
    const x = window ? samples[i] * window[i] : samples[i];
    const s = x + coeff * s1 - s2;
    s2 = s1;
    s1 = s;
  }
  return Math.max(0, (s1 * s1 + s2 * s2 - coeff * s1 * s2) / (n * n));
}

const windowCache = new Map<number, Float64Array>();
function hann(n: number): Float64Array {
  let w = windowCache.get(n);
  if (!w) {
    w = new Float64Array(n);
    for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
    windowCache.set(n, w);
  }
  return w;
}

/**
 * Energy-weighted geometric mean frequency inside `[lo, hi]` — a one-number stand-in
 * for a formant peak that does not need peak picking or a real spectral envelope.
 * Returns 0 when the band is empty.
 */
function formantCentroid(
  spectrum: Float64Array,
  band: readonly [number, number],
): number {
  let num = 0;
  let den = 0;
  for (let i = 0; i < PROBES.length; i++) {
    const f = PROBES[i];
    if (f < band[0] || f > band[1]) continue;
    const p = spectrum[i];
    num += p * Math.log(f);
    den += p;
  }
  return den > 0 ? Math.exp(num / den) : 0;
}

/**
 * Nearest-vowel weights for a measured formant pair, as a soft assignment so the
 * mouth cross-fades between shapes instead of snapping.
 */
export function visemesFromFormants(f1: number, f2: number, level: number): Viseme[] {
  if (!(f1 > 0) || !(f2 > 0) || level <= 0) {
    return VISEME_IDS.map((id) => ({ id, weight: 0 }));
  }
  const scores = VISEME_IDS.map((id) => {
    const [c1, c2] = VOWEL_FORMANTS[id];
    const d1 = Math.log(f1 / c1) / FORMANT_SIGMA;
    const d2 = Math.log(f2 / c2) / FORMANT_SIGMA;
    return Math.exp(-0.5 * (d1 * d1 + d2 * d2));
  });
  const total = scores.reduce((a, b) => a + b, 0);
  if (total <= 0) return VISEME_IDS.map((id) => ({ id, weight: 0 }));
  return VISEME_IDS.map((id, i) => ({ id, weight: (scores[i] / total) * level }));
}

export interface MouthAnalyserOptions {
  /** dBFS that reads as a closed mouth. */
  floorDb?: number;
  /** dBFS that reads as a fully open mouth. */
  ceilingDb?: number;
  /** Seconds for the mouth to open (one-pole time constant). */
  attack?: number;
  /** Seconds for the mouth to fall closed. Longer than attack, as mouths are. */
  release?: number;
  /** Normalised level below which the frame reads as silence, killing room hiss. */
  gate?: number;
  /** Emit viseme weights. `false` skips the spectral pass entirely. */
  visemes?: boolean;
}

/** Seconds a detected lip closure is reported for: two 60 Hz blocks, one 30 fps frame. */
const CLOSURE_HOLD = 0.04;

const DEFAULTS: Required<MouthAnalyserOptions> = {
  floorDb: -50,
  ceilingDb: -12,
  attack: 0.025,
  release: 0.12,
  gate: 0.06,
  visemes: true,
};

/**
 * Turns blocks of audio samples into {@link MouthFrame}s.
 *
 * Stateful only in the envelope follower — feed it blocks in time order and it
 * produces a mouth that opens fast and closes lazily, like a real one.
 */
export class MouthAnalyser {
  readonly options: Required<MouthAnalyserOptions>;
  private env = 0;
  private lastT: number | null = null;
  private shutFor = 0; // seconds a detected closure is still reported
  private spectrum: Float64Array;

  constructor(options: MouthAnalyserOptions = {}) {
    this.options = { ...DEFAULTS, ...options };
    this.spectrum = new Float64Array(PROBES.length);
  }

  /** Forget the envelope, e.g. when playback stops. */
  reset(): void {
    this.env = 0;
    this.lastT = null;
    this.shutFor = 0;
  }

  /**
   * @param samples Time-domain block, nominally -1..1.
   * @param sampleRate Hz.
   * @param t Seconds, monotonic. Deltas drive the attack/release shaping.
   */
  analyse(samples: Float32Array, sampleRate: number, t: number): MouthFrame {
    const o = this.options;
    const dt = this.lastT === null ? 1 / 60 : clamp(t - this.lastT, 1 / 1000, 0.1);
    this.lastT = t;

    const db = 20 * Math.log10(rms(samples) + 1e-9);
    const raw = clamp01((db - o.floorDb) / (o.ceilingDb - o.floorDb));
    // Gate first, then re-expand, so gated silence is a true zero rather than a floor.
    const target = raw < o.gate ? 0 : (raw - o.gate) / (1 - o.gate);

    // A stop consonant: the block falls to well under half the envelope while speech is going.
    // Held for CLOSURE_HOLD so a renderer sampling every other block still sees it.
    if (this.env > 0.2 && target < this.env * 0.4) this.shutFor = CLOSURE_HOLD;
    else this.shutFor = Math.max(0, this.shutFor - dt);
    const closure = this.shutFor > 0 || undefined;

    const tau = target > this.env ? o.attack : o.release;
    this.env += (target - this.env) * (1 - Math.exp(-dt / tau));
    const level = clamp01(this.env);

    if (!o.visemes) return { t, level, closure };

    const [f1, f2] = measureFormants(samples, sampleRate, this.spectrum);
    return { t, level, visemes: visemesFromFormants(f1, f2, level), closure };
  }
}

/**
 * The block's first and second formant as this analyser reads them (pre-emphasised centroids
 * inside each band). Exported for `tools/layered-avatar/vowel-calibration.ts`, which fits
 * {@link VOWEL_FORMANTS} to real voices with it.
 */
export function measureFormants(
  samples: Float32Array,
  sampleRate: number,
  spectrum: Float64Array = new Float64Array(PROBES.length),
): [f1: number, f2: number] {
  const window = hann(samples.length);
  for (let i = 0; i < PROBES.length; i++) {
    spectrum[i] = goertzelPower(samples, sampleRate, PROBES[i], window) * preEmphasis(PROBES[i]);
  }
  return [formantCentroid(spectrum, F1_BAND), formantCentroid(spectrum, F2_BAND)];
}

export interface MouthDriverOptions extends MouthAnalyserOptions {
  /** Analyser window size. 1024 @48k ≈ 21ms — enough for a 180Hz probe. */
  fftSize?: number;
}

/**
 * Live audio → mouth frames. Tap it onto whatever node plays the assistant's
 * voice; it only listens, it never alters the signal path.
 */
export class MouthDriver {
  readonly analyserNode: AnalyserNode;
  readonly analyser: MouthAnalyser;
  private readonly context: BaseAudioContext;
  private readonly source: AudioNode;
  private readonly buffer: Float32Array<ArrayBuffer>;
  private raf = 0;
  private disposed = false;

  constructor(source: AudioNode, options: MouthDriverOptions = {}) {
    this.context = source.context;
    this.source = source;
    this.analyser = new MouthAnalyser(options);
    this.analyserNode = this.context.createAnalyser();
    this.analyserNode.fftSize = options.fftSize ?? 1024;
    this.analyserNode.smoothingTimeConstant = 0; // we do our own, time-correct smoothing
    source.connect(this.analyserNode);
    this.buffer = new Float32Array(this.analyserNode.fftSize);
  }

  /** Tap a microphone or any other `MediaStream`. */
  static fromStream(
    stream: MediaStream,
    context: AudioContext,
    options?: MouthDriverOptions,
  ): MouthDriver {
    return new MouthDriver(context.createMediaStreamSource(stream), options);
  }

  /**
   * Tap an `<audio>`/`<video>` element. Also connects it to the speakers, since
   * routing an element into Web Audio otherwise silences it.
   */
  static fromElement(
    element: HTMLMediaElement,
    context: AudioContext,
    options?: MouthDriverOptions,
  ): MouthDriver {
    const node = context.createMediaElementSource(element);
    node.connect(context.destination);
    return new MouthDriver(node, options);
  }

  /** Sample the current audio and produce one frame. */
  read(): MouthFrame {
    this.analyserNode.getFloatTimeDomainData(this.buffer);
    return this.analyser.analyse(this.buffer, this.context.sampleRate, this.context.currentTime);
  }

  /** Call `onFrame` once per animation frame until {@link stop}. */
  start(onFrame: (frame: MouthFrame) => void): void {
    if (this.disposed || this.raf) return;
    const tick = () => {
      if (this.disposed) return;
      this.raf = requestAnimationFrame(tick);
      onFrame(this.read());
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.analyser.reset();
  }

  dispose(): void {
    this.stop();
    this.disposed = true;
    try {
      this.source.disconnect(this.analyserNode);
    } catch {
      // already torn down with its context
    }
  }
}

/** Frame for sources that only report loudness — no spectrum, so no visemes. */
export function frameFromLevel(t: number, level: number): MouthFrame {
  return { t, level: clamp01(level) };
}

/** A closed mouth. Handy for resetting a face when audio ends. */
export function silentFrame(t: number): MouthFrame {
  return { t, level: 0 };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
